<script lang="ts" setup>
import { computed, ref, useSlots } from 'vue';

import AppSvgIcon from '../appSvgIcon/AppSvgIcon.vue';

import type {
  AppTagIconProperties,
  AppTagSize,
  AppTagType,
} from './AppTag.model';

interface AppTagProps {
  type: AppTagType;
  textWrap?: boolean;
  icon?: AppTagIconProperties;
  size?: AppTagSize;
  clickable?: boolean;
}

const props = withDefaults(defineProps<AppTagProps>(), { textWrap: true });

interface AppTagEmits {
  (click: 'click'): void;
  (eventName: 'icon-click'): void;
}

const emit = defineEmits<AppTagEmits>();

const slots = useSlots();

const hasTitleSlot = computed(() => !!slots['title']);
const isTagHovered = ref<boolean>(false);
</script>

<template>
  <div
    class="tag"
    :class="[
      props.type,
      props.size,
      props.icon?.position === 'right' ? 'right-icon' : 'left-icon',
      { clickable: props.clickable },
      { 'has-title-slot': hasTitleSlot },
      { wrap: props.textWrap },
      { 'no-wrap': !props.textWrap },
    ]"
    @click="emit('click')"
    @mouseenter="isTagHovered = true"
    @mouseleave="isTagHovered = false"
  >
    <component
      :is="props.icon.clickable ? 'button' : 'span'"
      v-if="props.icon"
      class="tag-icon-wrapper"
      :class="{ clickable: props.icon.clickable }"
      @click="props.icon.clickable ? emit('icon-click') : null"
    >
      <AppSvgIcon
        class="tag-icon"
        :name="props.icon.name"
        :size="icon?.size"
        :color="isTagHovered ? icon?.hoverColor : icon?.color"
        :aria-label="props.icon.ariaLabel"
      />
    </component>
    <div v-if="hasTitleSlot" class="tag-title">
      <slot name="title" />
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '../../styles/media' as *;
@use '../../styles/fonts' as *;

.tag {
  --s-size-tag-height: 27px;

  border-radius: var(--border-radius-20);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-8) var(--spacing-12);
  color: var(--color);
  box-sizing: border-box;
  width: fit-content;
  @include font-body-s-light;

  &.clickable {
    cursor: pointer;
  }

  &.has-title-slot {
    gap: var(--spacing-4);
  }

  &.warning {
    background-color: var(--color-brand-summer);
    color: var(--color-soft-black);
  }

  &.grey {
    background-color: var(--color-neutral-30);
    color: var(--black);

    &.s {
      padding: var(--spacing-4) var(--spacing-8);
    }
  }

  &.grey-new {
    background-color: var(--color-neutral-30);
    color: var(--color-soft-black);

    &.s {
      padding: var(--spacing-4) var(--spacing-8);
    }
  }

  &.positive {
    background-color: var(--color-positive-70);
    color: var(--color-soft-black);
  }

  &.white {
    background-color: var(--color-white);
    color: var(--color-soft-black);
    box-shadow: 0px 3px 12px 0px rgba(0, 0, 0, 0.25);
  }

  &.info {
    background-color: var(--color-neutral-70);
    color: var(--color-white);
    border-radius: var(--border-radius-30);

    .tag-icon {
      width: 16px;
    }
  }

  &.purple {
    background-color: var(--color-brand-lilac);
    color: var(--color-neutral-70);
    @include font-body-s-bold;

    &.s {
      padding: var(--spacing-4) var(--spacing-8);
    }
  }

  &.blue {
    background-color: var(--color-brand-sky);
    color: var(--color-soft-black);
    @include font-label-s-light;
    &.s {
      padding: var(--spacing-4) var(--spacing-8);
    }
  }

  &.filter {
    min-height: var(--spacing-24);
    background-color: var(--color-white);
    border: 1px solid var(--color-neutral-30);
    padding: 0px var(--spacing-12);
    gap: var(--spacing-8);
    @include font-label-s-light;

    .tag-icon {
      color: var(--color-white);
      border-radius: 50%;
    }

    &.xs {
      min-height: var(--spacing-24);
    }

    &.s {
      min-height: var(--s-size-tag-height);
    }

    &.m {
      height: var(--spacing-32);
    }

    @include on-hover {
      background-color: var(--color-neutral-10);
      border-color: var(--color-overlay);
    }
  }

  &.enabled-filter {
    min-height: var(--spacing-24);
    background-color: var(--color-neutral-70);
    color: var(--color-white);
    padding: 0px var(--spacing-12);
    gap: var(--spacing-8);
    @include font-label-s-light;

    &.xs {
      min-height: var(--spacing-24);
    }

    &.s {
      min-height: var(--s-size-tag-height);
    }

    &.m {
      height: var(--spacing-32);
    }

    @include on-hover {
      background-color: var(--color-neutral-70);
    }
  }

  &.modern-filter {
    border: 1px solid var(--color-neutral-70);
    min-height: var(--spacing-24);
    background-color: var(--color-white);
    padding: 0px var(--spacing-12);
    gap: var(--spacing-8);

    @include font-label-s-bold;

    &.xs {
      min-height: var(--spacing-24);
    }

    &.s {
      min-height: var(--s-size-tag-height);
    }

    &.m {
      height: var(--spacing-32);
    }
  }

  &.left-icon {
    flex-direction: row;
  }

  &.right-icon {
    flex-direction: row-reverse;
  }

  &.wrap {
    &.tag-title {
      text-wrap: wrap;
    }
  }

  &.no-wrap {
    .tag-title {
      white-space: nowrap;
    }
  }
  .tag-icon-wrapper {
    padding: 0;
    border: none;
    background: transparent;
    .tag-icon {
      width: 18px;
      display: flex;
      align-items: center;
      justify-content: center;
      &.clickable {
        cursor: pointer;
      }
    }
  }
}
</style>
